import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '../store/store';
import DashboardLayout from './DashboardLayout';
import { showOfflineSaveAlertForCategory } from '../utils/offlineAlerts';
import { setFetchedCycles, selectFetchedCycles, addOfflineData } from '../store/CodeVerificationSlice';
import type { CodeVerificationCycleData } from '../Services/CodeVerificationRecord';
import { uploadFileToSharePoint, getFileDisplayData, type FileDisplayData } from '../Services/CodeVerificationFileUpload';
import { getAccessToken } from '../Services/getAccessToken';
import { removeFile } from '../store/CodeVerificationSlice';



const CodeVerificationRecord: React.FC = () => {
    console.log('CodeVerificationRecord component is rendering...');

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { plantTourId, selectedCycle } = useSelector((state: RootState) => state.planTour);
    const { user } = useSelector((state: RootState) => state.user);
    const {
        cycles,
        pendingSync,
        currentCycle: reduxCurrentCycle
    } = useSelector((state: RootState) => state.productMonitoring);

    // Get offline status from app state
    const isOfflineStarted = useSelector((state: RootState) => state.appState.isOfflineStarted);

    // Get Code Verification data from Redux
    const fetchedCycles = useSelector(selectFetchedCycles);

    console.log('Redux state loaded successfully:', {
        plantTourId,
        selectedCycle,
        user: user?.Name,
        cycles: cycles.length,
        pendingSync: pendingSync.length,
        isOfflineStarted,
        reduxCurrentCycle,
        codeVerificationCycles: fetchedCycles.length,
        fetchedCyclesData: fetchedCycles
    });

    // Form state for Code Verification
    const [formData, setFormData] = useState({
        sku: '',
        machineProof: '',
        majorDefectsRemarks: ''
    });

    // Start session form state
    const [startFormData, setStartFormData] = useState({
        executiveName: ''
    });

    // Session state
    const [isSessionStarted, setIsSessionStarted] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState('');
    const [expandedCompletedCycles, setExpandedCompletedCycles] = useState<Set<string>>(new Set());
    const [productFormExpandedByCycle, setProductFormExpandedByCycle] = useState<{ [key: number]: boolean }>({});

    // Loading state for fetching data
    const [isLoadingCycles, setIsLoadingCycles] = useState(false);

    // Local cycle management
    const [currentCycle, setCurrentCycle] = useState(1);

    // File upload state
    const [uploadedFiles, setUploadedFiles] = useState<FileDisplayData[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [selectedFileName, setSelectedFileName] = useState<string>('');
    const [viewingFile, setViewingFile] = useState<FileDisplayData | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);



    const handleStartSession = async () => {
        console.log('Starting code verification session with product:', selectedProduct, 'executive:', startFormData.executiveName);

        try {
            // Use the service to handle start session
            const { startSessionHandler } = await import('../Services/CodeVerificationRecord');
            await startSessionHandler(
                currentCycle,
                selectedProduct,
                startFormData.executiveName
            );

            setIsSessionStarted(true);
            setIsExpanded(true);
        } catch (error) {
            console.error('Error starting session:', error);
            alert('Error starting session. Please try again.');
        }
    };

    // Handle file upload
    const handleFileUpload = async (file: File) => {
        if (!plantTourId) {
            alert('Plant Tour ID is required for file upload');
            return;
        }

        setIsUploading(true);
        try {
            const tokenResult = await getAccessToken();
            const accessToken = tokenResult?.token || '';
            
            if (!accessToken) {
                throw new Error('Access token is missing. Please try again.');
            }
            
            console.log('Uploading file:', {
                fileName: file.name,
                fileSize: file.size,
                fileType: file.type,
                cycleNum: currentCycle,
                qualityTourId: plantTourId
            });
            
            const result = await uploadFileToSharePoint(currentCycle, plantTourId, accessToken, file);
            
            if (result.success) {
                // Refresh uploaded files list
                const files = getFileDisplayData(currentCycle);
                setUploadedFiles(files);
                
                // Clear file input
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                
                // Don't show alert if it's stored offline (will sync later)
                if (!result.message.includes('stored offline')) {
                    alert(result.message);
                }
            } else {
                alert(result.message);
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            const errorMessage = error instanceof Error ? error.message : 'Error uploading file. Please try again.';
            
            // If we're offline or access token failed, try to store file offline as fallback
            try {
                const { storeFileOffline, getFileDisplayData } = await import('../Services/CodeVerificationFileUpload');
                if (file && plantTourId) {
                    await storeFileOffline(currentCycle, file, plantTourId);
                    const files = getFileDisplayData(currentCycle);
                    setUploadedFiles(files);
                    alert(`File "${file.name}" stored offline for Cycle ${currentCycle}. Will upload when online.`);
                } else {
                    alert(errorMessage);
                }
            } catch (offlineError) {
                console.error('Error storing file offline:', offlineError);
                alert(errorMessage);
            }
        } finally {
            setIsUploading(false);
        }
    };

    // Handle file delete
    const handleDeleteFile = async (fileId: string) => {
        if (window.confirm('Are you sure you want to delete this file?')) {
            dispatch(removeFile(fileId));
            // Refresh uploaded files list
            const files = getFileDisplayData(currentCycle);
            setUploadedFiles(files);
        }
    };

    // Check if file is an image
    const isImageFile = (fileName: string): boolean => {
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
        return imageExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
    };

    const handleSave = async () => {
        console.log('=== HANDLE SAVE STARTED ===');
        console.log('Saving code verification data for cycle:', currentCycle, formData);
        console.log('Current offline status:', isOfflineStarted);

        // Check if we have any data to save
        const hasDataToSave = formData.sku.trim() !== '' || formData.machineProof.trim() !== '' || formData.majorDefectsRemarks.trim() !== '';
        if (!hasDataToSave) {
            alert('No data to save. Please enter at least one value.');
            return;
        }

        try {

            // Check if we're offline
            if (isOfflineStarted) {
                console.log('Processing in offline mode...');

                // Store data for offline sync in Code Verification slice
                const offlineData = {
                    cycleNo: currentCycle,
                    records: [{
                        cycleNum: currentCycle.toString(),
                        product: selectedProduct,
                        executiveName: startFormData.executiveName || 'N/A',
                        sku: formData.sku,
                        proof: formData.machineProof,
                        remarks: formData.majorDefectsRemarks
                    }],
                    timestamp: Date.now()
                };
                console.log('Storing offline data in Redux:', offlineData);
                dispatch(addOfflineData(offlineData));
                console.log('Offline data dispatched to Redux store');

                // Debug: Check Redux state after dispatch
                setTimeout(() => {
                    const currentState = (window as any).__REDUX_DEVTOOLS_EXTENSION__?.connect()?.getState();
                    console.log('Redux state after offline data dispatch:', currentState);
                }, 100);

                // Add the completed cycle to Redux so it shows in completed section
                const completedCycleData = {
                    cycleNum: currentCycle.toString(),
                    product: selectedProduct,
                    executiveName: startFormData.executiveName || 'N/A',
                    sku: formData.sku,
                    proof: formData.machineProof,
                    remarks: formData.majorDefectsRemarks
                };
                dispatch(setFetchedCycles([...fetchedCycles, completedCycleData]));

                showOfflineSaveAlertForCategory(currentCycle, 'Code Verification Record');
                setProductFormExpandedByCycle(prev => ({ ...prev, [currentCycle]: false }));
            } else {
                console.log('Processing in online mode...');

                // Collect attachment URLs for this cycle (files already uploaded to SharePoint)
                const cycleFiles = getFileDisplayData(currentCycle);
                const attachmentUrls = cycleFiles
                    .filter(f => !f.isOffline && f.fileUrl && f.fileUrl.startsWith('http'))
                    .map(f => f.fileUrl);

                // Use the service to save data
                const { collectEstimationDataCycleSave, saveSectionApiCall } = await import('../Services/CodeVerificationRecord');
                const { savedData } = await collectEstimationDataCycleSave(
                    currentCycle,
                    formData,
                    plantTourId || '',
                    user?.Name || '',
                    attachmentUrls
                );

                const result = await saveSectionApiCall(savedData);
                if (result.success) {
                    console.log('Data saved successfully to API');

                    // Add the completed cycle to Redux so it shows in completed section
                    const completedCycleData = {
                        cycleNum: currentCycle.toString(),
                        product: selectedProduct,
                        executiveName: startFormData.executiveName || 'N/A',
                        sku: formData.sku,
                        proof: formData.machineProof,
                        remarks: formData.majorDefectsRemarks
                    };
                    dispatch(setFetchedCycles([...fetchedCycles, completedCycleData]));

                    alert('Product saved. Add another product or click "Complete Cycle & Move to Next".');
                } else {
                    throw new Error(result.message);
                }
            }

            // Reset form data for next product (do NOT advance cycle - user must click Complete Cycle)
            setFormData({
                sku: '',
                machineProof: '',
                majorDefectsRemarks: ''
            });
            setProductFormExpandedByCycle(prev => ({ ...prev, [currentCycle]: false }));

        } catch (error) {
            console.error('Error saving data:', error);
            alert('Error saving data. Please try again.');
        }
    };

    // Format date as DD/MM/YYYY
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const formattedDate = `${day}/${month}/${year}`;

    const handleCompleteCycle = () => {
        setCurrentCycle(prev => Math.min(prev + 1, 9));
        setFormData({ sku: '', machineProof: '', majorDefectsRemarks: '' });
        setStartFormData({ executiveName: '' });
        setSelectedProduct('');
        setProductFormExpandedByCycle(prev => ({ ...prev, [currentCycle]: false }));
        setIsSessionStarted(false);
    };

    const toggleCompletedCycleExpansion = (cycleNum: string) => {
        setExpandedCompletedCycles(prev => {
            const newSet = new Set(prev);
            if (newSet.has(cycleNum)) {
                newSet.delete(cycleNum);
            } else {
                newSet.add(cycleNum);
            }
            return newSet;
        });
    };

    // Group fetchedCycles by cycleNum for display (multiple products per cycle)
    const cyclesByCycleNum = fetchedCycles.reduce((acc: { [key: string]: CodeVerificationCycleData[] }, c) => {
        const key = c.cycleNum;
        if (!acc[key]) acc[key] = [];
        acc[key].push(c);
        return acc;
    }, {});
    const completedCycleNums = Object.keys(cyclesByCycleNum).sort((a, b) => parseInt(a) - parseInt(b));
    const currentCycleProducts = fetchedCycles.filter(c => c.cycleNum === currentCycle.toString());
    const showProductSummary = currentCycleProducts.length >= 1 && !productFormExpandedByCycle[currentCycle];

    // Component initialization
    useEffect(() => {
        console.log('=== CODE VERIFICATION RECORD COMPONENT MOUNTED ===');
        console.log('Plant Tour ID:', plantTourId);
        console.log('Current cycles in Redux:', cycles.length);
        console.log('Is Offline:', isOfflineStarted);
        console.log('Fetched cycles from Redux:', fetchedCycles.length);

        // Fetch cycle data when online so PWA shows cycles completed in SharePoint (or other sources)
        if (plantTourId && !isOfflineStarted) {
            console.log('Plant Tour ID found and online, fetching latest cycle data from API...');
            fetchCycleDataFromAPI();
        } else if (isOfflineStarted) {
            console.log('Offline mode: using existing Redux data, skipping fetch');
        } else {
            console.log('No Plant Tour ID found, cannot fetch cycle data');
        }
    }, [plantTourId, isOfflineStarted]);

    // Load uploaded files for current cycle
    useEffect(() => {
        const files = getFileDisplayData(currentCycle);
        setUploadedFiles(files);
    }, [currentCycle]);

    // Set current cycle ONLY on initial mount when we have persisted data from Redux
    // Empty deps [] = runs once on mount only - NOT when user saves a product (which would wrongly advance the cycle)
    useEffect(() => {
        if (fetchedCycles.length === 0) {
            setCurrentCycle(1);
            return;
        }
        const maxCycleNumber = Math.max(...fetchedCycles.map(cycle => parseInt(cycle.cycleNum) || 1));
        setCurrentCycle(maxCycleNumber + 1);
        console.log(`Mount with persisted data: ${fetchedCycles.length} cycles. Setting current to: ${maxCycleNumber + 1}`);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- only on mount for persisted data

    // Function to fetch cycle data from API
    const fetchCycleDataFromAPI = async () => {
        try {
            setIsLoadingCycles(true);
            console.log('=== FETCHING CYCLE DATA FROM API ===');
            console.log('Plant Tour ID:', plantTourId);
            console.log('Is Offline:', isOfflineStarted);

            if (!plantTourId) {
                console.log('No Plant Tour ID available, skipping fetch');
                return;
            }

            const { fetchCycleData } = await import('../Services/CodeVerificationRecord');
            console.log('fetchCycleData function imported successfully');

            const fetchedData = await fetchCycleData(plantTourId);
            console.log('API Response - Fetched cycles from API:', fetchedData);
            console.log('API Response - Data type:', typeof fetchedData);
            console.log('API Response - Is array:', Array.isArray(fetchedData));

            // Ensure the data is properly typed
            const typedData = Array.isArray(fetchedData) ? fetchedData as CodeVerificationCycleData[] : [];
            dispatch(setFetchedCycles(typedData));

            if (typedData.length > 0) {
                const maxCycle = Math.max(...typedData.map(c => parseInt(c.cycleNum) || 1));
                setCurrentCycle(maxCycle + 1);
                console.log('API load: set currentCycle to', maxCycle + 1);
            }

            if (fetchedData && fetchedData.length > 0) {
                console.log('Successfully fetched', fetchedData.length, 'cycles');
                console.log('Cycle details:', fetchedData);
            } else {
                console.log('No cycles found for this plant tour');
            }
        } catch (error) {
            console.error('Error fetching cycle data:', error);
            console.error('Error details:', {
                message: error instanceof Error ? error.message : 'Unknown error',
                stack: error instanceof Error ? error.stack : 'No stack trace'
            });
            // Don't show alert for fetch errors, just log them
            setFetchedCycles([]);
        } finally {
            setIsLoadingCycles(false);
        }
    };

    return (
        <DashboardLayout>
            {/* Header Section */}
            <div className="bg-white px-3 sm:px-4 md:px-6 py-3 sm:py-4 mb-4 sm:mb-6 border-b border-gray-200 w-full">
                <div className="flex items-center justify-between gap-2">
                    {/* Back Button */}
                    <button
                        onClick={() => {
                            console.log('Back button clicked, navigating to previous page');
                            navigate(-1);
                        }}
                        className="flex items-center text-blue-600 hover:text-blue-800 transition-colors flex-shrink-0"
                    >
                        <span className="text-lg mr-1">&lt;</span>
                        <span className="font-medium text-sm sm:text-base">Back</span>
                    </button>

                    {/* Plant Tour ID */}
                    <div className="text-right min-w-0 flex-1">
                        <span className="text-gray-700 text-sm sm:text-base">Plant Tour ID: </span>
                        <span className="text-blue-600 font-medium text-sm sm:text-base break-all truncate">{plantTourId || 'N/A'}</span>
                    </div>
                </div>
            </div>

            {/* Code Verification Record Header */}
            <div className="bg-gray-100 px-3 sm:px-4 md:px-6 py-3 sm:py-4 mb-4 sm:mb-6 rounded-lg w-full">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                        <h1 className="text-base sm:text-lg md:text-xl font-bold text-gray-800">Code Verification Record</h1>
                        <div className="flex items-center gap-2 sm:gap-4">
                            <div className="bg-gray-200 rounded-full px-2 sm:px-3 py-1 flex items-center gap-1 sm:gap-2">
                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                <span className="text-xs sm:text-sm font-medium text-gray-700">{selectedCycle || 'Shift 1'}</span>
                            </div>
                            <span className="text-xs sm:text-sm text-gray-600">{formattedDate}</span>
                            {isOfflineStarted && (
                                <div className="bg-orange-100 border border-orange-300 rounded-full px-2 sm:px-3 py-1 flex items-center gap-1 sm:gap-2">
                                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                                    <span className="text-xs sm:text-sm font-medium text-orange-700">Offline Mode</span>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={fetchCycleDataFromAPI}
                            disabled={isLoadingCycles}
                            className="text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-1"
                        >
                            <svg className={`w-4 h-4 ${isLoadingCycles ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            {/* <span className="text-sm">Refresh</span> */}
                        </button>
                    </div>
                </div>
            </div>

            {/* Completed Cycles Display - grouped by cycle, multiple products per cycle */}
            {completedCycleNums.length > 0 && (
                <div className="space-y-4 mb-6">
                    {completedCycleNums.map((cycleNum) => {
                        const cycleProducts = cyclesByCycleNum[cycleNum] || [];
                        return (
                            <div key={cycleNum} className="bg-white rounded-lg shadow-sm border p-3 sm:p-4 md:p-6 w-full">
                                <div className="flex items-center justify-between mb-4 sm:mb-6">
                                    <div className="flex items-center gap-2">
                                        <h2 className="text-base sm:text-lg font-bold text-gray-800">Cycle {cycleNum}</h2>
                                        <span className="text-xs text-gray-600">({cycleProducts.length} product(s))</span>
                                    </div>
                                    <svg
                                        className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-500 cursor-pointer transition-transform ${expandedCompletedCycles.has(cycleNum) ? 'rotate-180' : ''}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        onClick={() => toggleCompletedCycleExpansion(cycleNum)}
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                                {expandedCompletedCycles.has(cycleNum) && (
                                    <div className="space-y-4">
                                        {cycleProducts.map((cycleData: CodeVerificationCycleData, idx: number) => (
                                            <div key={`${cycleData.cycleNum}-${idx}-${cycleData.product}`} className="border border-gray-200 rounded-lg p-3 sm:p-4 bg-white">
                                                <h3 className="text-sm font-bold text-red-600 mb-3">Summary - Row {idx + 1}</h3>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                                                    <div>
                                                        <div className="text-xs text-gray-500">Product</div>
                                                        <div className="text-sm font-medium text-gray-900">{cycleData.product || 'N/A'}</div>
                                                    </div>
                                                    <div>
                                                        <div className="text-xs text-gray-500">Executive Name</div>
                                                        <div className="text-sm font-medium text-gray-900">{cycleData.executiveName || 'N/A'}</div>
                                                    </div>
                                                </div>
                                                <div className="overflow-x-auto">
                                                    <table className="w-full border-collapse border border-gray-300">
                                                        <thead>
                                                            <tr className="bg-gray-50">
                                                                <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium">SKU</th>
                                                                <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium">Machine Proof</th>
                                                                <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium">Remarks</th>
                                                                <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium">Attachments</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr>
                                                                <td className="border border-gray-300 px-4 py-2">{cycleData.sku || 'N/A'}</td>
                                                                <td className="border border-gray-300 px-4 py-2">{cycleData.proof || 'N/A'}</td>
                                                                <td className="border border-gray-300 px-4 py-2">{cycleData.remarks || 'N/A'}</td>
                                                                <td className="border border-gray-300 px-4 py-2">
                                                                    {(() => {
                                                                        const raw = (cycleData as any).attachments;
                                                                        if (!raw) return 'No attachments';
                                                                        const str = String(raw).trim();
                                                                        if (!str) return 'No attachments';
                                                                        try {
                                                                            const parsed = JSON.parse(str);
                                                                            if (Array.isArray(parsed) && parsed.length > 0) {
                                                                                return parsed.map((item: { name?: string; url?: string; link?: string } | string, i: number) => {
                                                                                    const obj = typeof item === 'object' && item !== null ? item : { name: String(item), url: String(item).startsWith('http') ? String(item) : '' };
                                                                                    const url = obj.url || obj.link || (typeof obj.name === 'string' && obj.name.startsWith('http') ? obj.name : '');
                                                                                    const displayName = obj.name || obj.url || obj.link || 'Attachment';
                                                                                    const label = url ? (displayName === url ? (url.split('/').pop() || displayName) : displayName) : displayName;
                                                                                    if (!url) return <span key={i} className="block truncate max-w-[200px]">{label}</span>;
                                                                                    return (
                                                                                        <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline block truncate max-w-[200px]" title={label}>{label}</a>
                                                                                    );
                                                                                });
                                                                            }
                                                                        } catch (_) { /* not JSON */ }
                                                                        const urls = str.split(';').filter(Boolean);
                                                                        if (urls.length === 0) return 'No attachments';
                                                                        return urls.map((u: string, i: number) => {
                                                                            const url = u.trim();
                                                                            const name = url.split('/').pop() || url;
                                                                            return (
                                                                                <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline block truncate max-w-[200px]" title={url}>{name}</a>
                                                                            );
                                                                        });
                                                                    })()}
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}



            {/* Main Content - Current Cycle Section */}
            <div className="bg-white rounded-lg shadow-sm border p-3 sm:p-4 md:p-6 w-full">
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                    <h2 className="text-base sm:text-lg font-bold text-gray-800">Cycle {currentCycle}</h2>
                    <svg
                        className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-500 cursor-pointer transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        onClick={() => setIsExpanded(!isExpanded)}
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                </div>

                {/* Completed product(s) summary when 1+ products saved */}
                {showProductSummary ? (
                    <div className="mb-4">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-green-700 font-semibold">
                                    {currentCycleProducts.length} product(s) saved in Cycle {currentCycle}
                                </span>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-3">
                                <button
                                    onClick={() => {
                                        setProductFormExpandedByCycle(prev => ({ ...prev, [currentCycle]: true }));
                                        setIsSessionStarted(false);
                                        setSelectedProduct('');
                                        setStartFormData({ executiveName: '' });
                                    }}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium"
                                >
                                    Add New Product
                                </button>
                                <button
                                    onClick={handleCompleteCycle}
                                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium"
                                >
                                    Complete Cycle & Move to Next
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                <>
                {/* Start Session Form */}
                {!isSessionStarted && (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                            {/* Product Field */}
                            {/* <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Product</label>
                                <select 
                                    className="w-full border border-gray-300 rounded-md px-2 sm:px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    value={selectedProduct}
                                    onChange={(e) => setSelectedProduct(e.target.value)}
                                >
                                    <option value="Speciality Sauces">Speciality Sauces</option>
                                    <option value="Zesty Wasabi">Zesty Wasabi</option>
                                    <option value="Product 3">Product 3</option>
                                </select>
                            </div> */}
                            <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Product</label>
                                <input
                                    type="text"
                                    value={selectedProduct}
                                    onChange={(e) => setSelectedProduct(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-2 sm:px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter Product"
                                />
                            </div>

                            {/* Executive Name Field */}
                            <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Executive Name</label>
                                <input
                                    type="text"
                                    value={startFormData.executiveName}
                                    onChange={(e) => setStartFormData(prev => ({ ...prev, executiveName: e.target.value }))}
                                    className="w-full border border-gray-300 rounded-md px-2 sm:px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter executive name"
                                />
                            </div>
                        </div>

                        {/* Start Session Button */}
                        <div className="flex justify-end mt-4 sm:mt-6 md:mt-8">
                            <button
                                onClick={handleStartSession}
                                className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-md font-medium text-sm sm:text-base transition-colors"
                            >
                                Start Session
                            </button>
                        </div>
                    </div>
                )}

                {/* Form Content - Save Section */}
                {isSessionStarted && (
                    <div className="space-y-4">
                        {/* Product & Executive Name - Code Verification Record */}
                        <div className="bg-blue-100 rounded-lg p-4 mb-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <div className="text-sm text-gray-600 mb-1">Product</div>
                                    <div className="font-bold text-gray-800">{selectedProduct || 'N/A'}</div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-600 mb-1">Executive Name</div>
                                    <div className="font-bold text-gray-800">{startFormData.executiveName || 'N/A'}</div>
                                </div>
                            </div>
                        </div>

                        {/* Input Fields */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">SKU</label>
                                <input
                                    type="text"
                                    value={formData.sku}
                                    onChange={(e) => setFormData(prev => ({ ...prev, sku: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter value"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Machine Proof</label>
                                <input
                                    type="text"
                                    value={formData.machineProof}
                                    onChange={(e) => setFormData(prev => ({ ...prev, machineProof: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter value"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Major Defects and Remarks</label>
                            <textarea
                                value={formData.majorDefectsRemarks}
                                onChange={(e) => setFormData(prev => ({ ...prev, majorDefectsRemarks: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                rows={4}
                                placeholder="Type Here..."
                            />
                        </div>

                        {/* File Upload Section */}
                        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <h3 className="text-sm font-semibold text-gray-800 mb-3">Upload Attachment</h3>
                            
                            <div className="flex items-center gap-3 mb-2">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    id={`code-verification-attachment-${currentCycle}`}
                                    className="hidden"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                            setSelectedFileName(file.name);
                                        } else {
                                            setSelectedFileName('');
                                        }
                                    }}
                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx"
                                />
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm font-medium"
                                    disabled={isUploading}
                                >
                                    Choose File
                                </button>
                                <span className="text-sm text-gray-600">
                                    {selectedFileName || 'No file chosen'}
                                </span>
                                <button
                                    type="button"
                                    onClick={async () => {
                                        const file = fileInputRef.current?.files?.[0];
                                        if (file) {
                                            await handleFileUpload(file);
                                            setSelectedFileName(''); // Clear selected file name after upload
                                        }
                                    }}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium ml-auto"
                                    disabled={isUploading || !selectedFileName}
                                >
                                    {isUploading ? 'Uploading...' : 'Upload'}
                                </button>
                            </div>
                            
                            <p className="text-xs text-gray-500 mt-2">
                                You can upload supporting documents or images here.
                            </p>

                            {/* Display uploaded files */}
                            {uploadedFiles.length > 0 && (
                                <div className="mt-4">
                                    <h4 className="text-xs font-medium text-gray-700 mb-2">Uploaded Files:</h4>
                                    <div className="space-y-2">
                                        {uploadedFiles.map((file) => (
                                            <div key={file.id} className="flex items-center justify-between bg-white p-2 rounded border border-gray-200">
                                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                                    <span className="text-xs text-gray-600 truncate">{file.fileName}</span>
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    <button
                                                        type="button"
                                                        onClick={() => setViewingFile(file)}
                                                        className="text-xs text-blue-600 hover:underline px-2 py-1"
                                                    >
                                                        View
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteFile(file.id)}
                                                        className="text-xs text-red-600 hover:underline px-2 py-1"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end space-x-4">
                            <button
                                onClick={() => setIsSessionStarted(false)}
                                className="px-6 py-2 border border-blue-500 text-blue-500 rounded-md hover:bg-blue-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                            >
                                Save Product Checklist
                            </button>
                        </div>
                    </div>
                )}
                </>
                )}
            </div>

            {/* Image Viewer Modal */}
            {viewingFile && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setViewingFile(null)}>
                    <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] w-full overflow-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-800 truncate flex-1 mr-4">{viewingFile.fileName}</h3>
                            <button
                                onClick={() => setViewingFile(null)}
                                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                            >
                                ×
                            </button>
                        </div>
                        <div className="p-4">
                            {isImageFile(viewingFile.fileName) ? (
                                // Check if it's a SharePoint URL (requires authentication)
                                viewingFile.fileUrl.includes('sharepoint.com') ? (
                                    <div className="text-center py-8">
                                        <p className="text-gray-600 mb-4">Image stored in SharePoint. Click below to view:</p>
                                        <a
                                            href={viewingFile.fileUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-block px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                        >
                                            Open Image in New Tab
                                        </a>
                                    </div>
                                ) : (
                                    <img
                                        src={viewingFile.fileUrl}
                                        alt={viewingFile.fileName}
                                        className="max-w-full h-auto mx-auto rounded-lg"
                                        onError={(e) => {
                                            const target = e.target as HTMLImageElement;
                                            target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EImage not available%3C/text%3E%3C/svg%3E';
                                        }}
                                    />
                                )
                            ) : (
                                <div className="text-center py-8">
                                    <p className="text-gray-600 mb-4">Preview not available for this file type.</p>
                                    <a
                                        href={viewingFile.fileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-block px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                    >
                                        Open file in new tab
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
};

export default CodeVerificationRecord;
